<script>
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { mapGetters } from 'vuex';

export default {
  components: {
    SettingsSection,
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      automaticLabelingMode: 'off', // 'off', 'conversation_open', 'conversation_close', 'all_messages', 'first_message'
      labelConfigs: {},
      isUpdating: false,
    };
  },
  computed: {
    ...mapGetters({
      labels: 'labels/getLabels',
    }),
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
    labels() {
      this.initializeLabelConfigs();
    },
  },
  async mounted() {
    this.$store.dispatch('labels/get');
    this.setDefaults();
  },
  methods: {
    initializeLabelConfigs() {
      this.labels.forEach(label => {
        if (!this.labelConfigs[String(label.id)]) {
          this.labelConfigs[String(label.id)] = {
            enabled: false,
            description: '',
          };
        }
      });
    },
    setDefaults() {
      const labelingConfig = this.inbox?.automatic_labeling_config || {};
      this.automaticLabelingMode = labelingConfig.mode || 'off';
      if (labelingConfig.labels) {
        Object.keys(labelingConfig.labels).forEach(labelId => {
          const config = labelingConfig.labels[String(labelId)] || {};
          this.labelConfigs[String(labelId)] = {
            enabled: config.enabled || false,
            description: config.description || '',
          };
        });
      }
    },
    async updateInbox() {
      this.isUpdating = true;
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          automatic_labeling_config: {
            mode: this.automaticLabelingMode,
            labels: this.labelConfigs,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      }
      this.isUpdating = false;
    },
    getLabelColor(label) {
      return label.color || '#1f93ff';
    },
  },
};
</script>

<template>
  <div>
    <SettingsSection
      :title="$t('INBOX_MGMT.AUTOMATIC_LABELING.TITLE')"
      :sub-title="$t('INBOX_MGMT.AUTOMATIC_LABELING.SUB_TEXT')"
    >
      <div class="space-y-4">
        <div class="flex items-center">
          <input
            id="labeling-off"
            v-model="automaticLabelingMode"
            value="off"
            type="radio"
            class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
          />
          <label
            for="labeling-off"
            class="ml-3 block text-sm font-medium text-gray-700"
          >
            {{ $t('INBOX_MGMT.AUTOMATIC_LABELING.MODE.OFF') }}
          </label>
        </div>

        <div class="flex items-center">
          <input
            id="labeling-first-message"
            v-model="automaticLabelingMode"
            value="first_message"
            type="radio"
            class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
          />
          <label
            for="labeling-first-message"
            class="ml-3 block text-sm font-medium text-gray-700"
          >
            {{ $t('INBOX_MGMT.AUTOMATIC_LABELING.MODE.FIRST_MESSAGE') }}
          </label>
        </div>

        <div class="flex items-center">
          <input
            id="labeling-all-messages"
            v-model="automaticLabelingMode"
            value="all_messages"
            type="radio"
            class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
          />
          <label
            for="labeling-all-messages"
            class="ml-3 block text-sm font-medium text-gray-700"
          >
            {{ $t('INBOX_MGMT.AUTOMATIC_LABELING.MODE.ALL_MESSAGES') }}
          </label>
        </div>

        <div class="flex items-center">
          <input
            id="labeling-conversation-open"
            v-model="automaticLabelingMode"
            value="conversation_open"
            type="radio"
            class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
          />
          <label
            for="labeling-conversation-open"
            class="ml-3 block text-sm font-medium text-gray-700"
          >
            {{ $t('INBOX_MGMT.AUTOMATIC_LABELING.MODE.CONVERSATION_OPEN') }}
          </label>
        </div>

        <div class="flex items-center">
          <input
            id="labeling-conversation-close"
            v-model="automaticLabelingMode"
            value="conversation_close"
            type="radio"
            class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
          />
          <label
            for="labeling-conversation-close"
            class="ml-3 block text-sm font-medium text-gray-700"
          >
            {{ $t('INBOX_MGMT.AUTOMATIC_LABELING.MODE.CONVERSATION_CLOSE') }}
          </label>
        </div>

        <div class="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div class="flex items-start">
            <div class="flex-shrink-0">
              <svg
                class="h-5 w-5 text-yellow-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-yellow-800">
                {{
                  $t('INBOX_MGMT.AUTOMATIC_LABELING.BILLING_DISCLAIMER.TITLE')
                }}
              </h3>
              <p class="mt-1 text-sm text-yellow-700">
                {{
                  $t('INBOX_MGMT.AUTOMATIC_LABELING.BILLING_DISCLAIMER.MESSAGE')
                }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </SettingsSection>

    <SettingsSection
      :title="$t('INBOX_MGMT.AUTOMATIC_LABELING.LABEL_CONFIGURATION.TITLE')"
      :sub-title="
        $t('INBOX_MGMT.AUTOMATIC_LABELING.LABEL_CONFIGURATION.SUB_TEXT')
      "
    >
      <div v-if="labels.length === 0" class="text-center py-8">
        <p class="text-gray-500">
          {{ $t('INBOX_MGMT.AUTOMATIC_LABELING.NO_LABELS_MESSAGE') }}
        </p>
        <router-link
          :to="`/app/accounts/${$route.params.accountId}/settings/labels`"
          class="text-blue-600 hover:text-blue-800 underline"
        >
          {{ $t('INBOX_MGMT.AUTOMATIC_LABELING.CREATE_LABELS_LINK') }}
        </router-link>
      </div>

      <div v-else class="space-y-6">
        <div v-for="label in labels" :key="label.id" class="label-config-item">
          <div
            v-if="labelConfigs[String(label.id)]"
            class="flex items-center gap-2 mb-2"
          >
            <input
              :id="`enable-${label.id}`"
              v-model="labelConfigs[String(label.id)].enabled"
              type="checkbox"
            />
            <div
              class="w-3 h-3 rounded-full flex-shrink-0"
              :style="{ backgroundColor: getLabelColor(label) }"
            />
            <label
              :for="`enable-${label.id}`"
              class="font-medium text-gray-900"
            >
              {{ label.title }}
            </label>
            <span v-if="label.description" class="text-sm text-gray-500">
              ({{ label.description }})
            </span>
          </div>

          <div v-if="labelConfigs[String(label.id)]?.enabled" class="ml-6">
            <label
              :for="`description-${label.id}`"
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ $t('INBOX_MGMT.AUTOMATIC_LABELING.DESCRIPTION_LABEL') }}
            </label>
            <textarea
              :id="`description-${label.id}`"
              v-model="labelConfigs[String(label.id)].description"
              rows="3"
              class="w-full max-w-2xl resize-none"
              :placeholder="
                $t('INBOX_MGMT.AUTOMATIC_LABELING.DESCRIPTION_PLACEHOLDER', {
                  label: label.title,
                })
              "
            />
          </div>
        </div>
      </div>

      <div class="mt-6">
        <NextButton
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          :is-loading="isUpdating"
          @click="updateInbox"
        />
      </div>
    </SettingsSection>
  </div>
</template>

<style scoped lang="scss">
@import 'dashboard/assets/scss/variables';
@import 'dashboard/assets/scss/mixins';

.label-config-item {
  @apply border border-slate-200 dark:border-slate-700 rounded-lg p-4;
}

.settings-item {
  @apply block;
}
</style>
